/**
 * 演示：到家等待页（/arriving）没画好 → 再画一次 → 到家（CREATE-PET-REGEN-01，I 10-09：演示模式要能走通）。
 * 只有等待页在演示模式读这里（小窝、形象页的形象查询只在正式数据下发，见 home/PetFigure.tsx 的 usePetCharacter、me/LookPage.tsx）。
 * - 第一次读：没画好，原因是画像服务回的图带背景（background_not_removed，I108 才进契约；等待页按认不出的码走「再试一次」），可以再画一次；
 * - 点了再画一次：同一把幂等键只算一次（再点回同一个结果）；之后依次读到在排、在画、画好了（只要中性站姿，不要别的姿态）；
 * - 不放图：画好那一步的图地址只是个占位标记，等待页只用它核“有没有中性站姿”，不拿去显示；到家以后小窝照旧是演示世界的样板宠物；
 * - 每个宠物号各记各的；刷新页面（模块重新加载）就从头再演一遍。
 */
import type { CharacterCandidate, CharacterRegenerateResult, CharacterState, CharacterStatus } from "@/shared/contracts";

type Demo = { redrawn: boolean; reads: number; results: Map<string, CharacterRegenerateResult> };

const demos = new Map<string, Demo>();
let redraws = 0;
const QUEUED_AT = "2026-10-09T00:00:00Z";
/** 画像服务回的图带背景：I108 才进契约的码，这里按字符串给（候选的 reason 本来就是字符串） */
const BACKGROUND_NOT_REMOVED = "background_not_removed";

function demoOf(petId: string): Demo {
  let demo = demos.get(petId);
  if (!demo) {
    demo = { redrawn: false, reads: 0, results: new Map() };
    demos.set(petId, demo);
  }
  return demo;
}

function candidate(status: CharacterStatus, reason: string | null): CharacterCandidate {
  return { status, pose: "neutral_full", reference_version: 1, style_version: "demo", queued_at: QUEUED_AT, reason, task_id: "fx-arrive-task" };
}

export function fixtureArrivalCharacter(petId: string): CharacterState {
  const demo = demoOf(petId);
  const base = { pet_id: petId, active: null, candidate: null, blocked_reason: null, poses: [], required_poses: [] };
  if (!demo.redrawn) return { ...base, status: "failed", candidate: candidate("failed", BACKGROUND_NOT_REMOVED), can_regenerate: true };
  demo.reads += 1;
  if (demo.reads === 1) return { ...base, status: "queued", candidate: candidate("queued", null), can_regenerate: false };
  if (demo.reads === 2) return { ...base, status: "running", candidate: candidate("running", null), can_regenerate: false };
  return {
    ...base,
    status: "ready",
    can_regenerate: true,
    active: {
      character_set_id: "fx-arrive-set",
      pet_id: petId,
      revision: 1,
      reference_version: 1,
      style_version: "demo",
      published_at: QUEUED_AT,
      assets: [
        {
          asset_id: "fx-arrive-neutral",
          pose: "neutral_full",
          url: "fixture:arrive-demo/neutral_full",
          content_sha256: "fixture",
          content_type: "image/png",
          width: 512,
          height: 512,
          content_box: { left: 0.2, top: 0.1, right: 0.8, bottom: 0.95 },
          anchor: { x: 0.5, y: 0.95, measured: false },
          opaque_ratio: 0.4,
          has_alpha: true,
        },
      ],
    },
  };
}

export function fixtureArrivalRedraw(petId: string, idempotencyKey: string): CharacterRegenerateResult {
  const demo = demoOf(petId);
  const seen = demo.results.get(idempotencyKey);
  if (seen) return seen;
  // 已经在画（排上了、还没画好）：如实回“已经在准备了”，不再排一次
  const result: CharacterRegenerateResult = demo.redrawn && demo.reads < 3
    ? { accepted: false, status: "queued", task_id: null, reason: "already_queued" }
    : { accepted: true, status: "queued", task_id: `fx-arrive-redraw-${(redraws += 1)}`, reason: null };
  if (result.accepted) {
    demo.redrawn = true;
    demo.reads = 0;
  }
  demo.results.set(idempotencyKey, result);
  return result;
}
